import { RightOutlined } from '@ant-design/icons';
import { Popover } from 'antd';
import { clsx } from 'clsx';
import React, { useState, useCallback } from 'react';
import type { SourcesProps, SourcesRef } from '@/types/Sources';
import CarouselCard from './components/CarouselCard';
import useStyle from './styles';

const Sources: React.ForwardRefRenderFunction<SourcesRef, SourcesProps> = (props, ref) => {
  const {
    style,
    className,
    rootClassName,
    title,
    expandIconPosition = 'start',
    inline = false,
    expanded,
    defaultExpanded,
    onExpand,
    activeKey,
    items,
    onClick,
    popoverOverlayWidth = 300,
    ...restProps
  } = props;

  const styles = useStyle();

  const sourcesRef = React.useRef<HTMLDivElement>(null);
  React.useImperativeHandle(ref, () => ({
    nativeElement: sourcesRef.current!,
  }));

  // ============================ Collapsible ============================
  const [isExpand, setIsExpand] = useState<boolean>(defaultExpanded ?? true);
  const isControlled = expanded !== undefined;
  const actualExpanded = isControlled ? expanded : isExpand;

  const handleToggle = useCallback(() => {
    const newExpand = !actualExpanded;
    if (!isControlled) {
      setIsExpand(newExpand);
    }
    onExpand?.(newExpand);
  }, [actualExpanded, isControlled, onExpand]);

  const mergedCls = clsx(
    styles.sources,
    className,
    rootClassName,
    {
      [styles.sourcesInline]: inline,
    },
  );

  const ContentNode = items ? (
    <ul className={styles.sourcesList}>
      {items.map((item, index) => (
        <li
          key={item.key || index}
          className={styles.sourcesListItem}
          onClick={() => onClick?.(item)}
        >
          <a
            className={styles.sourcesLink}
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {item.icon && <span className={styles.sourcesLinkIcon}>{item.icon}</span>}
            <span className={styles.sourcesLinkTitle}>{item.title}</span>
          </a>
        </li>
      ))}
    </ul>
  ) : null;

  return (
    <div
      ref={sourcesRef}
      className={mergedCls}
      style={style}
      {...restProps}
    >
      {inline ? (
        <Popover
          content={
            <CarouselCard
              className={clsx(styles.sources, className)}
              style={style}
              activeKey={activeKey}
              prefixCls="sources"
              items={items}
              onClick={onClick}
            />
          }
          open={inline ? undefined : false}
          overlayStyle={{ width: popoverOverlayWidth }}
          placement="top"
          forceRender
        >
          <div className={styles.sourcesTitleWrapper}>
            <span>{title}</span>
          </div>
        </Popover>
      ) : (
        <>
          <div
            className={clsx(
              styles.sourcesTitleWrapper,
              expandIconPosition === 'end' && styles.sourcesIconPositionEnd,
            )}
            onClick={handleToggle}
          >
            <RightOutlined
              className={clsx(styles.sourcesTitleDownIcon, {
                [styles.sourcesTitleDownIconExpanded]: actualExpanded,
              })}
            />
            <span>{title}</span>
          </div>
          {actualExpanded && (
            <div className={styles.sourcesContent}>
              {ContentNode}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const ForwardSources = React.forwardRef(Sources);

if (process.env.NODE_ENV !== 'production') {
  ForwardSources.displayName = 'Sources';
}

export default ForwardSources;
export type { SourcesProps, SourcesItem, SourcesRef } from '@/types/Sources';